/**
 * Hook for generating word cloud masks using Canvas API
 * Ported from words_cloud_model.coffee calcMask() method
 */

import { useMemo, useEffect, useState } from 'react'

import { SVG_PATHS } from '@aytm/app/stats/page/question-views/components/word-cloud/constants/masks'

/**
 * Draw SVG path on canvas context
 * @param {CanvasRenderingContext2D} context
 * @param {Array} path - Array of path commands
 */
function drawPath(context, path) {
  for (const el of path) {
    switch (el[0]) {
      case 'm':
        context.moveTo(el[1], el[2])
        break
      case 'l':
        context.lineTo(el[1], el[2])
        break
      case 'c':
        context.bezierCurveTo(el[1], el[2], el[3], el[4], el[5], el[6])
        break
      case 'z':
        context.closePath()
        break
    }
  }
}

/**
 * Generate 32-bit mask array from canvas
 * @param {HTMLCanvasElement} canvas
 * @returns {{ mask32: number[], maskBound: number[] }}
 */
function generateMask32(canvas) {
  const w = canvas.width
  const h = canvas.height
  const context = canvas.getContext('2d', { willReadFrequently: true })

  // A canvas tainted by a CORS-mismatched custom mask throws SecurityError from getImageData.
  // Fall back to an empty mask so the cloud still lays out (graceful degrade per §11.4 / §12).
  let imageData
  try {
    imageData = context.getImageData(0, 0, w, h).data
  } catch {
    return { mask32: [], maskBound: [w, h, 0, 0] }
  }

  const mask32 = []
  const bound = [w, h, 0, 0] // minX, minY, maxX, maxY

  let j = 0
  let n = 0
  let x = 0
  let y = 0

  for (let i = 0; i < imageData.length; i += 4) {
    // Check if pixel is non-white (part of mask)
    const isInMask = imageData[i] !== 0
    n = (n << 1) | (isInMask ? imageData[i] : { 0: 1 })
    j++

    if (imageData[i] !== 0) {
      bound[0] = Math.min(x, bound[0])
      bound[1] = Math.min(y, bound[1])
      bound[2] = Math.max(x, bound[2])
      bound[3] = Math.max(y, bound[3])
    }

    x++
    if (x === w) {
      y++
      x = 0
    }

    if (j === 32) {
      mask32.push(n)
      j = 0
      n = 0
    }
  }

  return { mask32, maskBound: bound }
}

/**
 * Hook to generate mask for word cloud
 *
 * @param {string} mask - Mask type: '', 'circle', 'square', 'diamond', or SVG path name
 * @param {string} orientation - 'h' or 'v'
 * @param {number} width - Canvas width
 * @param {number} height - Canvas height
 * @param {string} customMaskPath - Optional custom mask path
 * @returns {{ mask32: number[], maskBound: number[], isReady: boolean }}
 */
export function useMaskRenderer(mask, orientation, width, height, customMaskPath = '') {
  const [customMaskImage, setCustomMaskImage] = useState(null)
  const [customMaskError, setCustomMaskError] = useState(false)

  // Handle custom mask image loading
  // eslint-disable-next-line consistent-return
  useEffect(() => {
    setCustomMaskError(false)

    if (!customMaskPath) {
      setCustomMaskImage(null)
      return
    }

    let canceled = false
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      if (canceled) return
      setCustomMaskImage(img)
    }
    img.onerror = () => {
      if (canceled) return
      setCustomMaskError(true)
    }
    img.src = customMaskPath

    return () => {
      canceled = true
      img.onload = null
      img.onerror = null
      // Abort in-flight fetch; guard against clearing a loaded image still held in state
      if (!img.complete) img.src = ''
    }
  }, [customMaskPath])

  return useMemo(() => {
    // No mask needed
    if ((!mask && !customMaskPath) || customMaskError) {
      return { mask32: [], maskBound: [], isReady: true }
    }

    // Custom mask not ready
    if (customMaskPath && !customMaskImage) {
      return { mask32: [], maskBound: [], isReady: false }
    }

    // Align width to 32-bit boundary
    const w = 32 * Math.floor(width / 32)
    const h = height

    if (w <= 0 || h <= 0) {
      return { mask32: [], maskBound: [], isReady: false }
    }

    // Create canvas
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const context = canvas.getContext('2d', { willReadFrequently: true })

    // Fill with white (outside mask)
    context.fillStyle = 'white'
    context.fillRect(0, 0, w, h)

    if (customMaskImage) {
      // Draw custom mask image
      context.drawImage(customMaskImage, 0, 0, w, h)
    } else {
      // context.scale(1.5, 1.5)

      // Draw predefined mask
      context.fillStyle = 'black'
      context.beginPath()

      switch (mask) {
        case 'circle': {
          context.arc(w / 2, h / 2, Math.min(w, h) / 2, 0, 2 * Math.PI)
          break
        }

        case 'square': {
          const side = Math.min(Math.floor((w * 3) / 4), Math.floor((h * 3) / 4))
          const x = Math.floor(w / 2) - Math.floor(side / 2)
          const y = Math.floor(h / 2) - Math.floor(side / 2)

          context.moveTo(x, y)
          context.lineTo(x + side, y)
          context.lineTo(x + side, y + side)
          context.lineTo(x, y + side)
          break
        }

        case 'diamond': {
          context.moveTo(w / 2, 0)
          context.lineTo(w, h / 2)
          context.lineTo(w / 2, h)
          context.lineTo(0, h / 2)
          break
        }

        default: {
          // SVG path mask
          const pathKey = `${mask}_${orientation}`
          if (SVG_PATHS[pathKey]) {
            drawPath(context, SVG_PATHS[pathKey])
          }
          break
        }
      }

      context.closePath()
      context.fill()
    }

    const { mask32, maskBound } = generateMask32(canvas)

    return { mask32, maskBound, isReady: true }
  }, [mask, orientation, width, height, customMaskPath, customMaskImage, customMaskError])
}

export default useMaskRenderer
